<template>
  <div class="result-ring">
    <div class="progress-circle" :style="{ width: size + 'px', height: size + 'px' }">
      <!-- Use a div with background-color for the outer ring -->
      <div class="outer-ring" :style="{
        backgroundColor: colorValue,
        borderRadius: '50%',
        width: size + 'px',
        height: size + 'px'
      }">
        <!-- White inner circle -->
        <div class="inner-circle" :style="{
          width: (size * 0.75) + 'px',
          height: (size * 0.75) + 'px',
          marginTop: (size * 0.125) + 'px',
          marginLeft: (size * 0.125) + 'px'
        }">
          <span class="percentage" :style="{
            fontSize: (size / 4.5) + 'px'
          }">{{ isNaN(percentage) ? $t('dashboard.na') : percentage + '%' }}</span>
        </div>
      </div>
    </div>
    <h3>{{ translatedCategory }}</h3>
    <p><span v-html="message"></span></p>
  </div>
</template>

<script>
export default {
  props: {
    category: {
      type: String,
      required: true
    },
    score: {
      type: Number,
      required: true
    },
    weights: {
      type: Object,
      default: () => ({})
    },
    size: {
      type: Number,
      default: 90
    }
  },
  data() {
    return {
      colorRanges: [
        { max: 70, color: 'red' },
        { max: 75, color: 'light-orange' },
        { max: 80, color: 'orange' },
        { max: 85, color: 'light-green' },
        { max: 99, color: 'medium-green' },
        { max: 100, color: 'dark-green' }
      ],
      // Map English category names to translation keys
      categoryTranslationKeys: {
        'Food Safety': 'category.food_safety',
        'Space Optimization': 'category.space_optimization',
        'Cost Savings': 'category.cost_savings',
        'Labor Efficiency': 'category.labor_efficiency',
        'Sustainability': 'category.sustainability',
        'Total Score': 'results.total_score'
      },
      // Message keys by category and score threshold
      categoryMessageKeys: {
        'Food Safety': [
          { max: 70, key: 'results.food_safety_work_to_be_done' },
          { max: 75, key: 'results.food_safety_room_to_perfect' },
          { max: 80, key: 'results.doing_good' },
          { max: 85, key: 'results.food_safety_almost_there' },
          { max: 99, key: 'results.food_safety_pretty_good' },
          { max: 100, key: 'results.simply_perfect' }
        ],
        'Cost Savings': [
          { max: 70, key: 'results.work_to_be_done' },
          { max: 75, key: 'results.cost_savings_room_to_improve' },
          { max: 80, key: 'results.doing_good' },
          { max: 85, key: 'results.cost_savings_almost_there' },
          { max: 99, key: 'results.cost_savings_pretty_good' },
          { max: 100, key: 'results.simply_perfect' }
        ],
        'Labor Efficiency': [
          { max: 70, key: 'results.work_to_be_done' },
          { max: 75, key: 'results.labor_efficiency_room_to_improve' },
          { max: 80, key: 'results.doing_good' },
          { max: 85, key: 'results.labor_efficiency_almost_there' },
          { max: 99, key: 'results.labor_efficiency_pretty_good' },
          { max: 100, key: 'results.simply_perfect' }
        ],
        'Space Optimization': [
          { max: 70, key: 'results.work_to_be_done' },
          { max: 75, key: 'results.space_optimization_room_to_improve' },
          { max: 80, key: 'results.doing_good' },
          { max: 85, key: 'results.space_optimization_almost_there' },
          { max: 99, key: 'results.space_optimization_pretty_good' },
          { max: 100, key: 'results.simply_perfect' }
        ],
        'Sustainability': [
          { max: 70, key: 'results.work_to_be_done' },
          { max: 75, key: 'results.sustainability_room_to_improve' },
          { max: 80, key: 'results.doing_good' },
          { max: 85, key: 'results.sustainability_almost_there' },
          { max: 99, key: 'results.sustainability_pretty_good' },
          { max: 100, key: 'results.simply_perfect' }
        ],
        'Total Score': [
          { max: 70, key: 'results.work_to_be_done' },
          { max: 75, key: 'results.total_room_to_improve' },
          { max: 80, key: 'results.doing_good' },
          { max: 85, key: 'results.total_almost_there' },
          { max: 99, key: 'results.total_pretty_good' },
          { max: 100, key: 'results.simply_perfect' }
        ]
      }
    };
  },
  computed: {
    weightedScore() {
      return this.score;
    },
    percentage() {
      return isNaN(this.score) ? 0 : Math.round(this.score);
    },
    colorClass() {
      const range = this.colorRanges.find(range => this.score <= range.max);
      return range ? range.color : 'dark-green';
    },
    colorValue() {
      // Convert color class to CSS color
      const colorMap = {
        'red': '#e40000',
        'light-orange': '#e35b00',
        'orange': '#e28b00',
        'light-green': '#bfa005',
        'medium-green': '#7ea20e',
        'dark-green': '#00a213'
      };
      return colorMap[this.colorClass] || '#00a213';
    },
    translatedCategory() {
      const key = this.categoryTranslationKeys[this.category];
      return key ? this.$t(key) : this.category;
    },
    message() {
      const categoryMessages = this.categoryMessageKeys[this.category] || this.categoryMessageKeys['Total Score'];
      if (!categoryMessages) {
        console.warn(`No messages found for category: ${this.category}`);
        return this.$t('results.simply_perfect');
      }
      const messageObj = categoryMessages.find(msg => this.weightedScore <= msg.max);
      return messageObj ? this.$t(messageObj.key) : this.$t('results.simply_perfect');
    }
  }
};
</script>

<style scoped>
.result-ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  text-align: center;
}

.progress-circle {
  position: relative;
  margin-bottom: 10px;
}

.outer-ring {
  position: relative;
  box-sizing: border-box;
}

.inner-circle {
  position: absolute;
  background-color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.percentage {
  font-weight: bold;
  color: #222;
  font-family: 'Rajdhani', sans-serif;
}

h3 {
  margin: 5px 0;
  font-weight: bold;
  font-size: 18px;
}

p {
  margin: 5px 0;
  font-size: 14px;
}
</style>